import { useState, useRef, useEffect, type FC, type ReactNode } from "react";
import type { Reservation, ReservationStatus } from "./reservations.types";
import { EditIcon, TrashIcon, MergeIcon, SplitIcon, ChevronDownIcon, DotsIcon, WarningIcon, TableGridIcon, CheckIcon,  ArrowLeftIcon , InfoIcon, UserIcon, LockIcon, MessageIcon, GiftIcon, HistoryIcon, PhoneIcon, CalendarIcon, TableIcon, ClockIcon, UsersIcon, MapPinIcon, MailIcon  } from "./Icons";
import Spinner from "./Components/Spinner";
import { XIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import {
  getReservation as apiGetReservation,
  updateReservation as apiUpdateReservation,
  markReservationSeated,
  markReservationCompleted,
  markReservationNoShow,
  cancelReservation,
  assignReservationTables,
  listReservationTableAssignments,
  listTables,
  listSections,
  type ReservationRecord,
} from "../lib/api/reservations.apis";


interface TableOption { id: string; name: string; capacity: number; sectionId: string; sectionName: string; }
interface StaffMember { id: string; name: string; role: string; }

// ─── MAPPER ───────────────────────────────────────────────────────────────────
const toReservation = (r: ReservationRecord, tableIds: string[]): Reservation => ({
  id: r._id,
  reference: (r as any).reference,
  guestName: r.guestName,
  phone: r.phone || "",
  email: r.email || "",
  occasion: r.occasion,
  specialRequest: r.specialRequest,
  acceptUpdates: r.acceptUpdates,
  partySize: r.partySize,
  startTime: r.startTime,
  endTime: r.endTime,
  source: r.source,
  notesInternal: r.notesInternal,
  status: r.status,
  tableIds,
  createdAt: r.createdAt || new Date().toISOString(),
  timezone: (r as any).timezone || "Africa/Nairobi",
  totalBookings: (r as any).totalBookings,
  lastBooking: (r as any).lastBooking,
});

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<ReservationStatus, { label: string; dot: string; text: string; bg: string; border: string }> = {
  confirmed: { label: "Confirmed", dot: "bg-green-500",  text: "text-green-700",  bg: "bg-green-50",  border: "border-green-200" },
  pending:   { label: "Pending",   dot: "bg-amber-400",  text: "text-amber-700",  bg: "bg-amber-50",  border: "border-amber-200" },
  seated:    { label: "Seated",    dot: "bg-blue-500",   text: "text-blue-700",   bg: "bg-blue-50",   border: "border-blue-200" },
  completed: { label: "Completed", dot: "bg-violet-500", text: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
  cancelled: { label: "Cancelled", dot: "bg-red-400",    text: "text-red-700",    bg: "bg-red-50",    border: "border-red-200" },
  no_show:   { label: "No Show",   dot: "bg-gray-400",   text: "text-gray-600",   bg: "bg-gray-100",  border: "border-gray-200" },
  waitlist:  { label: "Waitlist",  dot: "bg-orange-400", text: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
};

const SOURCE_LABELS: Record<string, string> = {
  walk_in: "Walk-In", email: "Email", gemio_app: "Gemio App",
  phone: "Phone", other: "Other",
};

const OCCASION_OPTIONS = ["", "Birthday", "Anniversary", "Business Dinner", "Date Night", "Graduation", "Other"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function formatDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: tz,
  });
}
function formatTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", timeZone: tz,
  });
}
function formatDuration(start: string, end: string): string {
  const mins = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

// ─── FIELD LABEL ─────────────────────────────────────────────────────────────
const FieldLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{children}</p>
);

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all placeholder-gray-300 box-border";
const textareaCls = `${inputCls} resize-y min-h-[80px]`;

// ─── STATUS DROPDOWN ─────────────────────────────────────────────────────────
const StatusDropdown: FC<{
  value: ReservationStatus;
  onChange: (s: ReservationStatus) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cfg = STATUS_CONFIG[value];

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => !disabled && setOpen(v => !v)}
        className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-semibold cursor-pointer transition-all
          ${cfg.bg} ${cfg.border} ${cfg.text} ${disabled ? "opacity-60 cursor-default" : "hover:opacity-80"}`}>
        <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
        {cfg.label}
        {!disabled && <ChevronDownIcon />}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl min-w-[180px] overflow-hidden z-50 py-1">
          {(Object.keys(STATUS_CONFIG) as ReservationStatus[]).map(s => {
            const c = STATUS_CONFIG[s];
            return (
              <button key={s} onClick={() => { onChange(s); setOpen(false); }}
                className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium border-none bg-transparent cursor-pointer transition-colors text-left hover:bg-gray-50
                  ${s === value ? "font-bold" : ""}`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
                <span className={c.text}>{c.label}</span>
                {s === value && <div className="ml-auto text-orange-500"><CheckIcon /></div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── DETAIL ROW ──────────────────────────────────────────────────────────────
const DetailRow: FC<{
  icon: ReactNode;
  label: string;
  value: ReactNode;
  editing?: boolean;
  editContent?: ReactNode;
}> = ({ icon, label, value, editing, editContent }) => (
  <div className="flex items-start gap-4 py-3.5 border-b border-gray-100 last:border-0">
    <div className="text-gray-400 mt-0.5 flex-shrink-0 w-5 flex justify-center">{icon}</div>
    <div className="flex-1 min-w-0">
      {editing && editContent ? editContent : (
        <span className="text-sm text-stone-800 font-medium">{value}</span>
      )}
    </div>
  </div>
);

// ─── TABLE ASSIGNMENT PANEL ───────────────────────────────────────────────────
const TableAssignPanel: FC<{
  assignedIds: string[];
  tables: TableOption[];
  onAssign: (ids: string[]) => Promise<void>;
  onClose: () => void;
}> = ({ assignedIds, tables, onAssign, onClose }) => {
  const [selected, setSelected] = useState<string[]>(assignedIds);
  const [loading, setLoading] = useState(false);

  const toggle = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const grouped = tables.reduce<Record<string, TableOption[]>>((acc, t) => {
    if (!acc[t.sectionName]) acc[t.sectionName] = [];
    acc[t.sectionName].push(t);
    return acc;
  }, {});

  const handle = async () => {
    setLoading(true);
    try { await onAssign(selected); onClose(); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/60 z-[1000] flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h3 className="m-0 text-base font-bold text-stone-900">Assign Tables</h3>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 border-none cursor-pointer rounded-xl p-1.5 flex items-center text-gray-500 transition-colors">
            <XIcon />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
          {Object.entries(grouped).map(([section, tbls]) => (
            <div key={section} className="mb-4 last:mb-0">
              <p className="m-0 mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{section}</p>
              <div className="flex flex-wrap gap-2">
                {tbls.map(t => {
                  const sel = selected.includes(t.id);
                  return (
                    <button key={t.id} onClick={() => toggle(t.id)}
                      className={`flex flex-col items-center px-3.5 py-2.5 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all
                        ${sel ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 bg-white text-stone-700 hover:border-orange-300"}`}>
                      {t.name}
                      <span className="text-[10px] font-normal opacity-60 mt-0.5">{t.capacity}p</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-4 py-2 text-sm font-semibold cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handle} disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-5 py-2 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2 disabled:opacity-60">
            {loading ? <Spinner /> : "Save Assignment"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CONFIRM ACTION MODAL ─────────────────────────────────────────────────────
const ConfirmActionModal: FC<{
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}> = ({ title, message, confirmLabel, confirmClass, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await onConfirm(); onClose(); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/60 z-[1100] flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between">
          <h3 className="m-0 text-base font-bold text-stone-900">{title}</h3>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 border-none cursor-pointer rounded-xl p-1.5 flex items-center text-gray-500 transition-colors">
            <XIcon />
          </button>
        </div>
        <div className="px-6 py-5">
          <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 mb-5">
            <div className="text-amber-500 flex-shrink-0"><WarningIcon /></div>
            <p className="m-0 text-sm text-amber-800 leading-relaxed">{message}</p>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-4 py-2 text-sm font-semibold cursor-pointer transition-colors">
              Cancel
            </button>
            <button onClick={handle} disabled={loading} className={`${confirmClass} text-white border-none rounded-xl px-5 py-2 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2 disabled:opacity-60`}>
              {loading ? <Spinner /> : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── QUICK ACTION BUTTON ──────────────────────────────────────────────────────
const ActionBtn: FC<{ label: string; onClick: () => void; variant?: "primary" | "warning" | "danger" | "neutral"; disabled?: boolean; loading?: boolean }> = ({
  label, onClick, variant = "neutral", disabled, loading,
}) => {
  const cls = {
    primary: "bg-slate-800 hover:bg-slate-700 text-white",
    warning: "bg-amber-500 hover:bg-amber-600 text-white",
    danger:  "bg-red-500 hover:bg-red-600 text-white",
    neutral: "bg-gray-100 hover:bg-gray-200 text-stone-700 border border-gray-200",
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled || loading}
      className={`${cls} border-none rounded-xl px-4 py-2 text-sm font-semibold cursor-pointer transition-colors flex items-center gap-2 disabled:opacity-50 whitespace-nowrap`}>
      {loading && <Spinner />}
      {label}
    </button>
  );
};

// ─── EDIT PATRON MODAL ────────────────────────────────────────────────────────
const EditPatronModal: FC<{
  reservation: Reservation;
  onClose: () => void;
  onSave: (data: Partial<Reservation>) => Promise<void>;
}> = ({ reservation, onClose, onSave }) => {
  const [form, setForm] = useState({
    guestName: reservation.guestName,
    phone:     reservation.phone,
    email:     reservation.email,
    acceptUpdates: reservation.acceptUpdates,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handle = async () => {
    if (!form.guestName.trim()) { setError("Guest name is required."); return; }
    setLoading(true);
    setError("");
    try { await onSave(form); onClose(); }
    catch { setError("Failed to save. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/60 z-[1100] flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-gray-100">
          <h3 className="m-0 text-lg font-bold text-stone-900">Edit Patron</h3>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 border-none cursor-pointer rounded-xl p-1.5 text-gray-500 transition-colors flex items-center">
            <XIcon />
          </button>
        </div>
        <div className="px-7 py-6 flex flex-col gap-4">
          <div>
            <FieldLabel>Full Name</FieldLabel>
            <input value={form.guestName} onChange={e => setForm(f => ({ ...f, guestName: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Phone</FieldLabel>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <FieldLabel>Email</FieldLabel>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div onClick={() => setForm(f => ({ ...f, acceptUpdates: !f.acceptUpdates }))}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all flex-shrink-0
                ${form.acceptUpdates ? "border-orange-500 bg-orange-500" : "border-gray-300 bg-white"}`}>
              {form.acceptUpdates && <span className="text-white text-xs font-bold leading-none">✓</span>}
            </div>
            <span className="text-sm font-medium text-stone-700">Accept booking updates</span>
          </label>
          {error && <p className="m-0 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-xl">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
              Cancel
            </button>
            <button onClick={handle} disabled={loading}
              className="bg-slate-800 hover:bg-slate-700 text-white border-none rounded-xl px-6 py-2.5 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2 disabled:opacity-60">
              {loading ? <Spinner /> : "Save Patron"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
interface ReservationDetailProps {
  reservationId?: string;
  restaurantId?: string;
  onBack?: () => void;
}

const ReservationDetail: FC<ReservationDetailProps> = ({
  reservationId: propReservationId,
  restaurantId: propRestaurantId,
  onBack,
}) => {
  const { restaurantId: paramRestaurantId, reservationId: paramReservationId } =
    useParams<{ restaurantId: string; reservationId: string }>();
  const restaurantId = propRestaurantId || paramRestaurantId || "";
  const reservationId = propReservationId || paramReservationId || "";
  const { accessToken } = useAuth();

  const [res, setRes]           = useState<Reservation | null>(null);
  const [tables, setTables]     = useState<TableOption[]>([]);
  const [fetchingRes, setFetchingRes] = useState(true);
  const [fetchError, setFetchError]   = useState<string | null>(null);
  const [editing, setEditing]   = useState(false);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [toast, setToast]       = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [showTablePanel, setShowTablePanel] = useState(false);
  const [showEditPatron, setShowEditPatron] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    title: string; message: string; confirmLabel: string; confirmClass: string; action: () => Promise<void>;
  } | null>(null);

  // Editable draft state
  const [draft, setDraft] = useState<Reservation | null>(null);

  // ── FETCH ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!restaurantId || !reservationId) return;
    const load = async () => {
      setFetchingRes(true);
      setFetchError(null);
      try {
        const [resData, tablesData, sectionsData, assignmentsData] = await Promise.all([
          apiGetReservation(restaurantId, reservationId, { token: accessToken }),
          listTables(restaurantId, undefined, { token: accessToken }),
          listSections(restaurantId, { token: accessToken }),
          listReservationTableAssignments(restaurantId, reservationId, { token: accessToken }),
        ]);

        const sectionMap = new Map(sectionsData.sections.map(s => [s._id, s.name]));
        const mappedTables: TableOption[] = tablesData.tables
          .filter(t => t.isActive !== false)
          .map(t => ({
            id: t._id,
            name: t.name,
            capacity: t.capacity,
            sectionId: t.sectionId,
            sectionName: sectionMap.get(t.sectionId) || "Unknown",
          }));

        const tableIds = assignmentsData.assignments
          .filter(a => a.tableId?._id)
          .map(a => a.tableId!._id);

        const mapped = toReservation(resData.reservation, tableIds);
        setRes(mapped);
        setDraft(mapped);
        setTables(mappedTables);
      } catch (e) {
        setFetchError(e instanceof Error ? e.message : "Failed to load reservation.");
      } finally {
        setFetchingRes(false);
      }
    };
    load();
  }, [restaurantId, reservationId, accessToken]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const startEditing = () => { setDraft({ ...res! }); setEditing(true); };
  const cancelEditing = () => setEditing(false);

  const saveEdits = async () => {
    if (!res || !draft) return;
    setSavingField("all");
    try {
      await apiUpdateReservation(restaurantId, res.id, draft, { token: accessToken });
      setRes({ ...draft });
      setEditing(false);
      showToast("Booking updated successfully.");
    } catch {
      showToast("Failed to save changes.", "error");
    } finally {
      setSavingField(null);
    }
  };

  const handleStatusChange = async (newStatus: ReservationStatus) => {
    if (!res || newStatus === res.status) return;

    const actionMap: Record<string, (() => Promise<any>) | undefined> = {
      seated:    () => markReservationSeated(restaurantId, res.id, { token: accessToken }),
      completed: () => markReservationCompleted(restaurantId, res.id, { token: accessToken }),
      no_show:   () => markReservationNoShow(restaurantId, res.id, { token: accessToken }),
      cancelled: () => cancelReservation(restaurantId, res.id, { token: accessToken }),
    };

    const action = actionMap[newStatus];
    if (action) {
      const cfg = STATUS_CONFIG[newStatus];
      setConfirmModal({
        title: `Mark as ${cfg.label}`,
        message: `Are you sure you want to mark this reservation as "${cfg.label}"? This action will update the booking status.`,
        confirmLabel: `Mark ${cfg.label}`,
        confirmClass: "bg-slate-800 hover:bg-slate-700",
        action: async () => {
          await action();
          setRes(r => r ? { ...r, status: newStatus } : r);
          showToast(`Status updated to ${cfg.label}.`);
        },
      });
    } else {
      await apiUpdateReservation(restaurantId, res.id, { status: newStatus }, { token: accessToken });
      setRes(r => r ? { ...r, status: newStatus } : r);
      showToast(`Status updated to ${STATUS_CONFIG[newStatus].label}.`);
    }
  };

  const handleAssignTables = async (tableIds: string[]) => {
    if (!res) return;
    await assignReservationTables(restaurantId, res.id, { tableIds }, { token: accessToken });
    const names = tableIds.map(id => tables.find(t => t.id === id)?.name ?? id);
    setRes(r => r ? { ...r, tableIds } : r);
    showToast(`Tables assigned: ${names.join(", ")}`);
  };

  const handleSavePatron = async (data: Partial<Reservation>) => {
    if (!res) return;
    await apiUpdateReservation(restaurantId, res.id, data, { token: accessToken });
    setRes(r => r ? { ...r, ...data } : r);
    showToast("Patron info updated.");
  };

  if (fetchingRes) return (
    <div className="min-h-full bg-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-3 text-sm text-gray-400">Loading reservation…</p>
      </div>
    </div>
  );

  if (fetchError) return (
    <div className="min-h-full bg-slate-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center max-w-sm">
        <p className="text-sm font-semibold text-red-600 mb-4">{fetchError}</p>
        <button onClick={onBack} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
          Go Back
        </button>
      </div>
    </div>
  );

  if (!res || !draft) return null;

  const assignedTableNames = (res.tableIds || [])
    .map(id => tables.find(t => t.id === id)?.name ?? id)
    .join(", ") || "—";

  const sectionName = (() => {
    const sectionNames = [...new Set((res.tableIds || []).map(id => tables.find(t => t.id === id)?.sectionName))];
    return sectionNames.filter(Boolean).join(", ") || res.section || "—";
  })();

  const isTerminal = res.status === "completed" || res.status === "cancelled" || res.status === "no_show";

  return (
    <div className="min-h-full bg-slate-100 flex justify-center py-8 px-4">
      <div className="w-full max-w-2xl">
        {/* ── TOP HEADER ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
            <button onClick={onBack}
              className="text-gray-500 hover:text-stone-800 bg-transparent border-none cursor-pointer flex items-center gap-1.5 text-sm font-medium transition-colors p-0">
              <ArrowLeftIcon />
            </button>

            {/* Table badges + guest name */}
            <div className="flex items-center gap-3 flex-1 justify-center">
              <div className="flex gap-1.5">
                {(res.tableIds || []).length > 0
                  ? (res.tableIds || []).map(id => {
                    const t = tables.find(x => x.id === id);
                    return (
                      <span key={id}
                        className="px-3 py-1 rounded-lg border-2 border-green-500 text-green-700 text-sm font-bold bg-green-50">
                        {t?.name ?? id}
                      </span>
                    );
                  })
                  : <span className="text-xs text-gray-400 italic">No table</span>
                }
              </div>
              <span className="text-lg font-bold text-stone-900">{res.guestName}</span>
            </div>

            <button onClick={editing ? cancelEditing : startEditing}
              className={`flex items-center gap-1.5 text-sm font-semibold border-none cursor-pointer rounded-xl px-3 py-1.5 transition-colors
                ${editing ? "bg-gray-100 hover:bg-gray-200 text-stone-600" : "bg-orange-50 hover:bg-orange-100 text-orange-600"}`}>
              {editing ? <><XIcon /> Cancel</> : <><EditIcon /> Edit</>}
            </button>
          </div>

          {/* Reference */}
          <div className="text-center py-2 border-b border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400 font-medium tracking-wide">ref: {res.reference}</span>
          </div>

          {/* Body */}
          <div className="px-6 py-2">
            {/* Status */}
            <div className="py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
              <StatusDropdown value={res.status} onChange={handleStatusChange} disabled={isTerminal || editing} />
              {editing && (
                <button onClick={saveEdits} disabled={savingField === "all"}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-5 py-2 text-sm font-bold cursor-pointer transition-colors disabled:opacity-60">
                  {savingField === "all" ? <Spinner /> : <CheckIcon />}
                  Save Changes
                </button>
              )}
            </div>

            {/* Date */}
            <DetailRow icon={<CalendarIcon />} label="Date" value={formatDate(res.startTime, res.timezone)}
              editing={editing}
              editContent={
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Start Date & Time</FieldLabel>
                    <input type="datetime-local"
                      value={draft.startTime.slice(0, 16)}
                      onChange={e => setDraft(d => ({ ...d, startTime: e.target.value + ":00.000Z" }))}
                      className={inputCls} />
                  </div>
                  <div>
                    <FieldLabel>End Date & Time</FieldLabel>
                    <input type="datetime-local"
                      value={draft.endTime.slice(0, 16)}
                      onChange={e => setDraft(d => ({ ...d, endTime: e.target.value + ":00.000Z" }))}
                      className={inputCls} />
                  </div>
                </div>
              }
            />

            {/* Table */}
            <DetailRow icon={<TableIcon />} label="Table" value={assignedTableNames}
              editing={editing}
              editContent={
                <div>
                  <FieldLabel>Assigned Tables</FieldLabel>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-800 font-medium flex-1">{assignedTableNames}</span>
                    <button onClick={() => setShowTablePanel(true)}
                      className="text-orange-500 hover:text-orange-600 text-xs font-semibold bg-orange-50 hover:bg-orange-100 border-none cursor-pointer rounded-lg px-3 py-1.5 transition-colors">
                      Change
                    </button>
                  </div>
                </div>
              }
            />

            {/* Time */}
            <DetailRow icon={<ClockIcon />} label="Time"
              value={`${formatTime(res.startTime, res.timezone)}  ${res.timezone}  ·  ${formatDuration(res.startTime, res.endTime)}`}
            />

            {/* Party Size */}
            <DetailRow icon={<UsersIcon />} label="Party Size"
              value={`${res.partySize} Adult${res.partySize > 1 ? "s" : ""}`}
              editing={editing}
              editContent={
                <div>
                  <FieldLabel>Party Size</FieldLabel>
                  <input type="number" min={1} max={100}
                    value={draft.partySize}
                    onChange={e => setDraft(d => ({ ...d, partySize: Number(e.target.value) }))}
                    className={`${inputCls} max-w-[120px]`} />
                </div>
              }
            />

            {/* Section */}
            <DetailRow icon={<MapPinIcon />} label="Section" value={sectionName} />

            {/* Occasion */}
            {(res.occasion || editing) && (
              <DetailRow icon={<GiftIcon />} label="Occasion"
                value={res.occasion || "—"}
                editing={editing}
                editContent={
                  <div>
                    <FieldLabel>Occasion</FieldLabel>
                    <select value={draft.occasion || ""} onChange={e => setDraft(d => ({ ...d, occasion: e.target.value }))} className={inputCls}>
                      {OCCASION_OPTIONS.map(o => <option key={o} value={o}>{o || "None"}</option>)}
                    </select>
                  </div>
                }
              />
            )}

            {/* Source */}
            <DetailRow icon={<InfoIcon />} label="Source"
              value={SOURCE_LABELS[res.source] || res.source}
              editing={editing}
              editContent={
                <div>
                  <FieldLabel>Booking Channel</FieldLabel>
                  <select value={draft.source} onChange={e => setDraft(d => ({ ...d, source: e.target.value }))} className={inputCls}>
                    {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              }
            />
          </div>

          {/* Created on footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
            <p className="m-0 text-xs text-gray-400">
              This booking was created on <span className="font-medium text-gray-500">{res.createdAt.slice(0, 10)} {res.timezone}</span>
            </p>
          </div>
        </div>

        {/* ── BOOKING COMMENTS ───────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <MessageIcon />
            <h3 className="m-0 text-sm font-bold text-stone-900 uppercase tracking-wide">Booking Comments</h3>
          </div>
          <div className="px-6 py-4 flex flex-col gap-4">
            {/* Guest request */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Guest Request</p>
                <span className="text-[10px] text-gray-300">(Visible to patron)</span>
              </div>
              {editing ? (
                <textarea value={draft.specialRequest || ""} onChange={e => setDraft(d => ({ ...d, specialRequest: e.target.value }))}
                  placeholder="Guest request or comment (e.g. window seat, allergy)..."
                  className={textareaCls} />
              ) : (
                <p className="m-0 text-sm text-stone-700 leading-relaxed bg-gray-50 rounded-xl px-4 py-3 min-h-[48px]">
                  {res.specialRequest || <span className="text-gray-300 italic">No guest request</span>}
                </p>
              )}
            </div>

            {/* Internal note */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <LockIcon />
                <p className="m-0 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Internal Note</p>
                <span className="text-[10px] text-gray-300">(Not visible to patron)</span>
              </div>
              {editing ? (
                <textarea value={draft.notesInternal || ""} onChange={e => setDraft(d => ({ ...d, notesInternal: e.target.value }))}
                  placeholder="Internal team note..."
                  className={textareaCls} />
              ) : (
                <p className="m-0 text-sm text-stone-700 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 min-h-[48px]">
                  {res.notesInternal || <span className="text-gray-300 italic">No internal note</span>}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── ABOUT GUEST ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="m-0 text-sm font-bold text-stone-900 uppercase tracking-wide">
              About {res.guestName.toUpperCase()}
            </h3>
          </div>

          {/* Guest stats */}
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <HistoryIcon />
              <span>Total Bookings: <strong className="text-stone-800">{res.totalBookings ?? 1}</strong></span>
            </div>
            <span className="text-gray-200">|</span>
            <span>Last Booking: <strong className="text-stone-800">{res.lastBooking ?? res.createdAt.slice(0, 10)}</strong></span>
          </div>

          {/* Contact info */}
          <div className="px-6 py-4 flex flex-col gap-3">
            <div className="flex items-center gap-3 py-2.5 border-b border-gray-100">
              <div className="text-gray-400 w-5 flex justify-center"><MailIcon /></div>
              <span className="text-sm text-stone-800 font-medium">{res.email || <span className="text-gray-300 italic">No email</span>}</span>
            </div>
            <div className="flex items-center gap-3 py-2.5">
              <div className="text-gray-400 w-5 flex justify-center"><PhoneIcon /></div>
              <span className="text-sm text-stone-800 font-medium">{res.phone || <span className="text-gray-300 italic">No phone</span>}</span>
            </div>
          </div>

          {/* Patron actions */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
            {/* <button
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-stone-800 border-2 border-gray-200 hover:border-gray-300 rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
              <HistoryIcon /> View Booking History
            </button> */}
            <button onClick={() => setShowEditPatron(true)}
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border-none rounded-xl px-5 py-2.5 text-sm font-bold cursor-pointer transition-colors">
              <UserIcon /> Edit Patron
            </button>
          </div>
        </div>

        {/* ── QUICK ACTIONS ──────────────────────────────────────── */}
        {!isTerminal && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="m-0 text-sm font-bold text-stone-900 uppercase tracking-wide">Quick Actions</h3>
            </div>
            <div className="px-6 py-4 flex flex-wrap gap-2">
              {res.status === "confirmed" && (
                <ActionBtn label="✓ Mark Seated" variant="primary"
                  onClick={() => setConfirmModal({
                    title: "Mark as Seated",
                    message: "This will update the reservation status to Seated and notify the floor team.",
                    confirmLabel: "Mark Seated",
                    confirmClass: "bg-blue-500 hover:bg-blue-600",
                    action: async () => { await markReservationSeated(restaurantId, res.id, { token: accessToken }); setRes(r => r ? { ...r, status: "seated" } : r); showToast("Marked as Seated."); },
                  })} />
              )}
              {res.status === "seated" && (
                <ActionBtn label="✓ Mark Completed" variant="primary"
                  onClick={() => setConfirmModal({
                    title: "Mark as Completed",
                    message: "This will close the reservation and mark the table as available.",
                    confirmLabel: "Mark Completed",
                    confirmClass: "bg-violet-500 hover:bg-violet-600",
                    action: async () => { await markReservationCompleted(restaurantId, res.id, { token: accessToken }); setRes(r => r ? { ...r, status: "completed" } : r); showToast("Marked as Completed."); },
                  })} />
              )}
              {(res.status === "confirmed" || res.status === "pending") && (
                <ActionBtn label="No Show" variant="warning"
                  onClick={() => setConfirmModal({
                    title: "Mark as No Show",
                    message: "Are you sure you want to mark this reservation as a No Show?",
                    confirmLabel: "Mark No Show",
                    confirmClass: "bg-amber-500 hover:bg-amber-600",
                    action: async () => { await markReservationNoShow(restaurantId, res.id, { token: accessToken }); setRes(r => r ? { ...r, status: "no_show" } : r); showToast("Marked as No Show."); },
                  })} />
              )}
              <ActionBtn label="Cancel Booking" variant="danger"
                onClick={() => setConfirmModal({
                  title: "Cancel Reservation",
                  message: "Are you sure you want to cancel this reservation? The guest will be notified if updates are enabled.",
                  confirmLabel: "Cancel Booking",
                  confirmClass: "bg-red-500 hover:bg-red-600",
                  action: async () => { await cancelReservation(restaurantId, res.id, { token: accessToken }); setRes(r => r ? { ...r, status: "cancelled" } : r); showToast("Reservation cancelled."); },
                })} />
              <ActionBtn label="Reassign Table" variant="neutral" onClick={() => setShowTablePanel(true)} />
            </div>
          </div>
        )}

        {/* ── STAFF ASSIGNMENT ───────────────────────────────────── */}
        {/* <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="m-0 text-sm font-bold text-stone-900 uppercase tracking-wide">Assigned Staff</h3>
          </div>
          <div className="px-6 py-4">
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={res.staffId || ""}
                onChange={async e => {
                  const staffId = e.target.value;
                  await api.assignStaff(res.id, staffId);
                  setRes(r => ({ ...r, staffId }));
                  showToast(`Staff assigned: ${MOCK_STAFF.find(s => s.id === staffId)?.name ?? staffId}`);
                }}
                className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 transition-all">
                <option value="">— Unassigned —</option>
                {MOCK_STAFF.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                ))}
              </select>
            </div>
          </div>
        </div> */}
      </div>
 
      {/* ── MODALS ────────────────────────────────────────────────── */}
      {showTablePanel && (
        <TableAssignPanel
          assignedIds={res.tableIds || []}
          tables={tables}
          onAssign={handleAssignTables}
          onClose={() => setShowTablePanel(false)}
        />
      )}

      {showEditPatron && (
        <EditPatronModal
          reservation={res}
          onClose={() => setShowEditPatron(false)}
          onSave={handleSavePatron}
        />
      )}

      {confirmModal && (
        <ConfirmActionModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onClose={() => setConfirmModal(null)}
          onConfirm={async () => { await confirmModal.action(); setConfirmModal(null); }}
        />
      )}

      {/* ── TOAST ─────────────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[2000] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-white text-sm font-semibold
          ${toast.type === "success" ? "bg-stone-900" : "bg-red-500"}`}>
          {toast.type === "success" ? <CheckIcon /> : <WarningIcon />}
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default ReservationDetail;